// 1:1 port of prototype/components/core/Chip.jsx (design D3): same names, same
// prop contract (prototype/components/core/Chip.d.ts), styled only from token CSS
// vars.
//
// Deliberate deviation from the kit (recorded in
// openspec/changes/client-scaffold/prototypes.md and mirrored back into
// prototype/components/core/Chip.jsx in this same change, per the
// prototype/CLAUDE.md sync contract): Chip is a pill button (`border-radius:
// var(--r-pill)`) and the design language commits every pill button to a ≥44px
// hit target (prototype/CLAUDE.md, `--hit-target` in tokens/spacing.css), but the
// kit's `minHeight` was 36px. Bumped to `var(--hit-target)` (44px) here so the
// ported component satisfies the client-app "ported component ... valid hit
// targets" scenario, and the kit was corrected to match rather than left to drift.

import type { CSSProperties, ReactNode } from 'react';

export interface ChipProps {
  /** Filled when true; `color` sets the fill (e.g. a bee-type token). */
  selected?: boolean;
  color?: string;
  icon?: ReactNode;
  children?: ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
}

export function Chip({ selected, color, icon, children, onClick, style }: ChipProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'var(--font-body)',
        fontWeight: 700,
        fontSize: 13,
        minHeight: 'var(--hit-target)',
        padding: '0 14px',
        borderRadius: 'var(--r-pill)',
        cursor: 'pointer',
        background: selected ? (color ?? 'var(--ink-1)') : 'var(--surface-card)',
        color: selected ? '#FFFDF6' : 'var(--ink-2)',
        border: `1px solid ${selected ? (color ?? 'var(--ink-1)') : 'var(--line-strong)'}`,
        transition: 'all var(--dur-fast)',
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export interface BadgeProps {
  tone?: 'accent' | 'danger' | 'success' | 'info' | 'neutral';
  children?: ReactNode;
  style?: CSSProperties;
}

const TONES: Record<NonNullable<BadgeProps['tone']>, [string, string]> = {
  accent: ['var(--accent-soft)', 'var(--accent-strong)'],
  danger: ['var(--danger-soft)', 'var(--danger)'],
  success: ['var(--success-soft)', 'var(--success)'],
  info: ['var(--info-soft)', 'var(--info)'],
  neutral: ['var(--paper-1)', 'var(--ink-2)'],
};

export function Badge({ tone = 'accent', children, style }: BadgeProps) {
  const [bg, fg] = TONES[tone] ?? TONES.accent;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: 'var(--font-body)',
        fontWeight: 800,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-caps)',
        padding: '3px 10px',
        borderRadius: 'var(--r-pill)',
        background: bg,
        color: fg,
        ...style,
      }}
    >
      {children}
    </span>
  );
}
